import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';

import { Spinner } from './Spinner.js';

/**
 * `danger` is a **platform** colour, not a brand one (§11.6).
 *
 * That is the whole reason this variant exists as a first-class option rather
 * than a class someone passes in: a destructive action has to look destructive
 * in every tenant, and if it were `bg-primary` a customer whose brand is red
 * would make "Save" look like "Disconnect" and vice versa. Criterion 8 compares
 * the computed colour of this button across two maximally different brands and
 * requires them to be identical.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'action';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  /**
   * §16.3.1: the fill and its foreground are a pair the API **measured**
   * (§11.6). `hover:bg-primary/90` would re-derive the background and leave the
   * measured foreground on a colour nobody checked. Dimming the element instead
   * moves both together, so the ratio is preserved whatever the tenant's hue.
   */
  primary: 'bg-primary text-primary-fg hover:brightness-95 disabled:opacity-60',
  secondary:
    'bg-surface text-body border border-line hover:bg-surface-alt hover:border-line-strong',
  ghost: 'bg-transparent text-body hover:bg-surface-alt',
  danger: 'bg-danger text-danger-fg hover:brightness-95 disabled:opacity-60',
  /**
   * Activate. Green rather than the brand, for the reason the estate's Activate
   * link is green: red on the operation this panel exists to perform reads as a
   * warning about it. Same measured pair as the fills above — `--color-action`
   * with `--color-action-fg`, 5.69:1.
   */
  action: 'bg-action text-action-fg hover:brightness-95 disabled:opacity-60',
};

/**
 * Elevation is kept OUT of `VARIANT_CLASS` on purpose.
 *
 * `brandColourUse.spec.ts` reads every quoted class string containing
 * `bg-primary` and requires `text-primary-fg` in the same string, so the two
 * measured halves of the pair can never drift apart in a diff. Splitting the
 * shadow into its own map keeps those strings exactly as the scan expects them
 * and keeps this decision — "a filled button sits on the page, an outline does
 * not" — legible on its own.
 *
 * Only the filled variants take it: a shadow under a transparent ghost button
 * draws a rectangle where the reader can see no button.
 */
const VARIANT_ELEVATION: Record<ButtonVariant, string> = {
  primary: 'shadow-[var(--elevation-1)]',
  secondary: '',
  ghost: '',
  danger: 'shadow-[var(--elevation-1)]',
  action: 'shadow-[var(--elevation-1)]',
};

export type ButtonSize = 'sm' | 'md';

/**
 * 36px is the control height the rest of §16.2 is built around; 28px is for a
 * control that sits *inside* another object — a 40px table row, the context
 * bar, a card header — where a full-height button reads as the object itself
 * rather than as an action on it.
 */
const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: 'h-7 gap-1.5 px-3 text-[length:var(--text-sm)]',
  md: 'h-9 gap-2 px-4 text-[length:var(--text-sm)]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  /** Defaults to the 36px control; `sm` is for buttons inside a row or a bar. */
  size?: ButtonSize;
  /**
   * An optional leading glyph. Decorative by construction — it is rendered
   * `aria-hidden`, so the button's accessible name stays exactly its text and
   * every `getByRole('button', { name })` in the suite keeps matching.
   */
  icon?: LucideIcon;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  loading = false,
  size = 'md',
  icon: Icon,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps): ReactElement {
  return (
    <button
      // Explicit, because an unspecified <button> inside a <form> submits it,
      // which has surprised every codebase that omitted it.
      type={rest.type ?? 'button'}
      disabled={disabled === true || loading}
      // `aria-busy` rather than swapping the label: replacing the text would
      // move focus context out from under a screen reader mid-action.
      aria-busy={loading || undefined}
      // The transition names its properties rather than using `transition-all`:
      // `all` would animate the layout properties too, so a button that changes
      // width when its label changes would slide instead of redraw. `filter` is
      // in the list because `hover:brightness-95` is how the measured brand pair
      // is dimmed without re-deriving either half of it.
      className={`inline-flex items-center justify-center rounded-[var(--radius-md)] font-medium transition-[background-color,border-color,box-shadow,filter,transform] duration-[var(--motion-fast)] ease-[var(--ease-out)] active:translate-y-[0.5px] disabled:cursor-not-allowed disabled:opacity-70 disabled:shadow-none ${
        SIZE_CLASS[size]
      } ${VARIANT_CLASS[variant]} ${VARIANT_ELEVATION[variant]} ${className}`}
      {...rest}
    >
      {loading ? (
        <Spinner label="" />
      ) : Icon ? (
        // 14px, so the glyph reads as a mark beside the label rather than as a
        // second, competing element. It inherits `currentColor`, which is what
        // keeps it inside the variant's measured pair.
        <Icon aria-hidden="true" size={14} strokeWidth={2} className="shrink-0" />
      ) : null}
      {children}
    </button>
  );
}
